import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconChevronDown, IconMessageReply, IconPaperclip, IconX } from '@tabler/icons-react';
import type { VhodyashcheeUvedomlenie } from '../obrashcheniya/api';
import { ObshchiyChat } from '../obrashcheniya/components/ObshchiyChat';
import { ZnachokKanala } from '../obrashcheniya/components/ZnachokKanala';
import { otvet as raskrytOtvet, poVremeni, prinyat, prodlit, PUSTAYA, ubratVidimye, zakryt, type Kartochka, type Stopka } from './gruppy';
import { prochitatNastroyki } from './nastroyki';
import { vidimyeSeychas } from './vidimyeDialogi';
import { podklyuchit, vzyatDialog, ZHDAT_VIDNO_MS, type Poslanie, type Svyaz } from './vkladki';
import { sygratVhodyashchee } from './zvuk';
import './uvedomleniya.css';

/**
 * ВХОДЯЩЕЕ УВЕДОМЛЕНИЕ СПРАВА ВНИЗУ (`s720`, приёмка владельца 06.10.2026).
 *
 * Слова задания: «клиент, филиал, канал, наша линия, текст/вложение, Ответить и Открыть точную
 * переписку. Ответ раскрывает общий мини-чат с контекстом и черновиком; аренда по s719, само
 * уведомление её не захватывает. Группировка по диалогу, максимум 3 карточки, одно
 * уведомление/звук между вкладками, без перехвата фокуса. Скрытие через 10 секунд, при наведении
 * пауза, раскрытый ответ остаётся. Закрытие не означает прочтение. Не дублировать активный
 * видимый диалог, соблюдать права и маски телефонов. В быстром ответе Enter — перенос,
 * Ctrl+Enter/кнопка — отправка. Всплытие и звук отключаемы».
 *
 * ⛔ УВЕДОМЛЕНИЕ НИЧЕГО НЕ МЕНЯЕТ НА СЕРВЕРЕ. Показ, закрытие и раскрытие ответа — дела экрана:
 * «прочитано» ставит только переписка, блокировку редактирования берёт только поле ответа (тем же
 * хуком, что в любом другом месте чата), когда человек поставил в него курсор.
 *
 * ⛔ ФОКУС НЕ ОТБИРАЕТСЯ. Карточка появляется без автофокуса и не модальна: человек, набирающий
 * текст в другом месте, продолжает набирать. Курсор в поле ответа встаёт только по его нажатию
 * «Ответить».
 *
 * Правила стопки — `gruppy.ts`; одна вкладка на показ и звук — `vkladki.ts`.
 */
const ZAPASNYE: Record<string, string> = {
  'uvedomlenie.otvetit': 'Ответить',
  'uvedomlenie.otkryt': 'Открыть переписку',
  'uvedomlenie.zakryt': 'Скрыть уведомление',
  'uvedomlenie.svernut': 'Свернуть ответ',
  'uvedomlenie.eshchyo': 'и ещё сообщений: {n}',
  'uvedomlenie.eshchyo_dialogov': 'Ещё диалогов с новыми сообщениями: {n}',
  'uvedomlenie.polucheno_na': 'Получено на',
  'uvedomlenie.vlozhenie': 'Вложение',
};

/** Входящее, не показанное пока вкладка была не на виду, ещё новость столько времени. */
const OTLOZHENNOE_ZHIVYOT_MS = 60_000;

export function VhodyashchieUvedomleniya({ userId }: { userId: number }) {
  const navigate = useNavigate();
  const [stopka, setStopka] = useState<Stopka>(PUSTAYA);
  const [slova, setSlova] = useState<Record<string, string>>({});
  const naPauze = useRef(new Set<number>());
  const svyaz = useRef<Svyaz | null>(null);
  const otlozhennye = useRef<VhodyashcheeUvedomlenie[]>([]);
  const pokazano = useRef(new Set<number>());
  const vidnoGde = useRef(new Map<number, number>());
  const derzhim = useRef(new Map<number, () => void>());

  const t = useCallback((kod: string, vars?: Record<string, string | number>) => {
    const syroe = slova[kod] ?? ZAPASNYE[kod] ?? kod;

    return vars === undefined ? syroe : Object.entries(vars).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), syroe);
  }, [slova]);

  // Показать пачку в ЭТОЙ вкладке: по диалогу — только если право показа взяли мы.
  const pokazat = useCallback(async (soobshcheniya: VhodyashcheeUvedomlenie[]) => {
    const granica = Date.now() - 2000;
    const svezhie = soobshcheniya.filter(
      (s) => !pokazano.current.has(s.id) && (vidnoGde.current.get(s.obrashchenie_id) ?? 0) < granica,
    );
    if (svezhie.length === 0 || !prochitatNastroyki(userId).vsplytie) return;

    if (document.visibilityState !== 'visible') {
      // Вкладку не видно — карточку увидит только воздух. Ждём: станет видимой — покажем свежее.
      otlozhennye.current = [...otlozhennye.current, ...svezhie].slice(-60);

      return;
    }

    const poDialogam = new Map<number, VhodyashcheeUvedomlenie[]>();
    for (const s of svezhie) poDialogam.set(s.obrashchenie_id, [...(poDialogam.get(s.obrashchenie_id) ?? []), s]);

    for (const [dialog, pachka] of poDialogam) {
      if (!derzhim.current.has(dialog)) {
        let otpustit: () => void = () => {};
        const derzhat = new Promise<void>((gotovo) => { otpustit = gotovo; });
        if (!(await vzyatDialog(dialog, derzhat))) {
          otpustit();
          continue;
        }
        derzhim.current.set(dialog, otpustit);
      }

      pachka.forEach((s) => pokazano.current.add(s.id));
      svyaz.current?.skazat({ tip: 'pokazano', ids: pachka.map((s) => s.id) });
      setStopka((bylo) => prinyat(bylo, pachka, Date.now(), vidimyeSeychas()));
    }
  }, [userId]);

  useEffect(() => {
    const onPoslanie = (p: Poslanie) => {
      if (p.tip === 'vidno') {
        vidnoGde.current.set(p.obrashchenieId, Date.now());
        setStopka((bylo) => ubratVidimye(bylo, new Set([p.obrashchenieId])));

        return;
      }

      if (p.tip === 'pokazano') {
        p.ids.forEach((id) => pokazano.current.add(id));
        otlozhennye.current = otlozhennye.current.filter((s) => !p.ids.includes(s.id));

        return;
      }

      if (Object.keys(p.slova).length > 0) setSlova((bylo) => ({ ...bylo, ...p.slova }));
      if (p.soobshcheniya.length === 0) return;

      // Диалог открыт и на виду здесь — говорим остальным «вижу»: карточки и звука по нему не будет.
      const vidimye = vidimyeSeychas();
      const ostalnye: VhodyashcheeUvedomlenie[] = [];
      for (const s of p.soobshcheniya) {
        if (vidimye.has(s.obrashchenie_id)) {
          vidnoGde.current.set(s.obrashchenie_id, Date.now());
          pokazano.current.add(s.id);
          svyaz.current?.skazat({ tip: 'vidno', obrashchenieId: s.obrashchenie_id });
        } else {
          ostalnye.push(s);
        }
      }

      // Даём соседним вкладкам время сказать «вижу», потом показываем.
      if (ostalnye.length > 0) window.setTimeout(() => void pokazat(ostalnye), ZHDAT_VIDNO_MS);
    };

    // Звук — дело ведущей вкладки: один раз на пачку, если хоть один её диалог никто не видит.
    const onPachka = (soobshcheniya: VhodyashcheeUvedomlenie[]) => {
      window.setTimeout(() => {
        const granica = Date.now() - 2000;
        const estNevidimyy = soobshcheniya.some((s) => (vidnoGde.current.get(s.obrashchenie_id) ?? 0) < granica);
        if (estNevidimyy && prochitatNastroyki(userId).zvuk) sygratVhodyashchee();
      }, ZHDAT_VIDNO_MS);
    };

    svyaz.current = podklyuchit(onPoslanie, onPachka);

    const stalaVidimoy = () => {
      if (document.visibilityState !== 'visible' || otlozhennye.current.length === 0) return;
      const granica = Date.now() - OTLOZHENNOE_ZHIVYOT_MS;
      const svezhie = otlozhennye.current.filter((s) => new Date(s.kogda).getTime() >= granica);
      otlozhennye.current = [];
      if (svezhie.length > 0) void pokazat(svezhie);
    };
    document.addEventListener('visibilitychange', stalaVidimoy);

    // Срок карточек и «диалог открыли — карточка не нужна». Память показанного не растёт без конца.
    const chasy = window.setInterval(() => {
      setStopka((bylo) => ubratVidimye(poVremeni(bylo, Date.now(), naPauze.current), vidimyeSeychas()));
      if (pokazano.current.size > 4000) pokazano.current = new Set([...pokazano.current].slice(-2000));
    }, 500);

    const derzhimSeychas = derzhim.current;

    return () => {
      document.removeEventListener('visibilitychange', stalaVidimoy);
      window.clearInterval(chasy);
      svyaz.current?.ostanovit();
      svyaz.current = null;
      derzhimSeychas.forEach((otpustit) => otpustit());
      derzhimSeychas.clear();
    };
  }, [pokazat, userId]);

  // Карточка ушла с экрана — право показа диалога свободно: следующее входящее покажет любая вкладка.
  useEffect(() => {
    const naEkrane = new Set(stopka.kartochki.map((k) => k.obrashchenieId));
    for (const [dialog, otpustit] of derzhim.current) {
      if (!naEkrane.has(dialog)) {
        otpustit();
        derzhim.current.delete(dialog);
      }
    }
  }, [stopka]);

  if (stopka.kartochki.length === 0) return null;

  const otkryt = (id: number) => {
    setStopka((bylo) => zakryt(bylo, id));
    navigate(`/obrashcheniya?obrashchenie=${id}`);
  };

  return (
    <div className="ops-skin uved-stopka" role="region" aria-label={t('uvedomlenie.polucheno_na')} aria-live="polite" data-testid="uvedomleniya">
      {stopka.zaPredelom.length > 0 && (
        <div className="uved-eshchyo" data-testid="uved-eshchyo-dialogov">
          {t('uvedomlenie.eshchyo_dialogov', { n: stopka.zaPredelom.length })}
        </div>
      )}
      {stopka.kartochki.map((k) => (
        <KartochkaUvedomleniya
          key={k.obrashchenieId}
          k={k}
          t={t}
          onPauza={(da) => {
            if (da) {
              naPauze.current.add(k.obrashchenieId);
            } else {
              naPauze.current.delete(k.obrashchenieId);
              setStopka((bylo) => prodlit(bylo, k.obrashchenieId, Date.now()));
            }
          }}
          onZakryt={() => {
            naPauze.current.delete(k.obrashchenieId);
            setStopka((bylo) => zakryt(bylo, k.obrashchenieId));
          }}
          onOtvet={(raskryt) => setStopka((bylo) => raskrytOtvet(bylo, k.obrashchenieId, raskryt, Date.now()))}
          onOtkryt={() => otkryt(k.obrashchenieId)}
          onDrugoyDialog={otkryt}
        />
      ))}
    </div>
  );
}

function KartochkaUvedomleniya({ k, t, onPauza, onZakryt, onOtvet, onOtkryt, onDrugoyDialog }: {
  k: Kartochka;
  t: (kod: string, vars?: Record<string, string | number>) => string;
  onPauza: (da: boolean) => void;
  onZakryt: () => void;
  onOtvet: (raskryt: boolean) => void;
  onOtkryt: () => void;
  onDrugoyDialog: (id: number) => void;
}) {
  const s = k.poslednee;
  const korob = useRef<HTMLDivElement>(null);

  // Курсор в поле ответа — только после нажатия «Ответить»: это действие самого человека.
  useEffect(() => {
    if (!k.otvet) return;
    const taymer = window.setTimeout(() => korob.current?.querySelector<HTMLTextAreaElement>('[data-testid="pole-otveta"]')?.focus(), 150);

    return () => window.clearTimeout(taymer);
  }, [k.otvet]);

  return (
    <div
      ref={korob}
      className="uved-kartochka"
      data-testid="uved-kartochka"
      data-dialog={k.obrashchenieId}
      data-otvet={k.otvet ? 'da' : 'net'}
      onMouseEnter={() => onPauza(true)}
      onMouseLeave={() => onPauza(false)}
      onFocus={() => onPauza(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onPauza(false);
      }}
    >
      <div className="uved-verh">
        <ZnachokKanala kind={s.kanal.kind} color="var(--ops-primary, #683dff)" razmer={28} />
        <div className="uved-kto">
          <b data-testid="uved-klientka">{s.klientka}</b>
          <span data-testid="uved-otkuda">{[s.filial, s.kanal.slovo].filter(Boolean).join(' · ')}</span>
        </div>
        <span className="uved-vremya">{s.vremya}</span>
        <button type="button" className="uved-krestik" onClick={onZakryt} aria-label={t('uvedomlenie.zakryt')} title={t('uvedomlenie.zakryt')} data-testid="uved-zakryt">
          <IconX size={20} />
        </button>
      </div>

      {s.liniya !== null && (
        <div className="uved-liniya" data-testid="uved-liniya">{t('uvedomlenie.polucheno_na')} {s.liniya}</div>
      )}

      {!k.otvet && (
        <div className="uved-tekst" data-testid="uved-tekst">
          {s.vlozhenie !== null && (
            <span className="uved-vlozhenie" data-testid="uved-vlozhenie"><IconPaperclip size={18} /> {s.vlozhenie.slovo}</span>
          )}
          {s.tekst !== null && <span>{s.tekst}</span>}
          {k.soobshcheniy > 1 && <span className="uved-schyot" data-testid="uved-eshchyo">{t('uvedomlenie.eshchyo', { n: k.soobshcheniy - 1 })}</span>}
        </div>
      )}

      {k.otvet && (
        <div className="uved-chat" data-testid="uved-chat">
          <ObshchiyChat obrashchenieId={k.obrashchenieId} mesto="mini" onOtkryt={onDrugoyDialog} />
        </div>
      )}

      <div className="uved-knopki">
        <button type="button" className="uved-knopka glavnaya" onClick={() => onOtvet(!k.otvet)} aria-expanded={k.otvet} data-testid="uved-otvetit">
          {k.otvet ? <><IconChevronDown size={18} /> {t('uvedomlenie.svernut')}</> : <><IconMessageReply size={18} /> {t('uvedomlenie.otvetit')}</>}
        </button>
        <button type="button" className="uved-knopka" onClick={onOtkryt} data-testid="uved-otkryt">
          {t('uvedomlenie.otkryt')}
        </button>
      </div>
    </div>
  );
}
